import { useContext, useState } from 'react';
import { GlobalContext } from '../../../store/GlobalState';
import { Table, Container, Row, Col, Button, Modal } from "react-bootstrap";
import UserToggle from '../../Users/UserToggle';
import DeleteIcon from '../../Icons/delete.png';
import axios from 'axios';
import { apiUrl } from '../../../utilities/requestHelpers';
import './UsersPage.css';
import { createToast } from '../../../utilities/toastHelpers';

const UsersPage = () => {
  const { isAdmin, jwtToken, users, toggleToasts } = useContext(GlobalContext);

  // State for delete modal and control
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const userDisplayValues = {
    base: 'User',
    manager: 'Manager'
  };

  // Open confirmation modal
  const handleOpenDeleteModal = (userId) => {
    setSelectedUserId(userId);
    setShowDeleteModal(true);
  };

  // Close modal and reset state
  const handleCloseDeleteModal = () => {
    setSelectedUserId(null);
    setShowDeleteModal(false);
    setIsDeleting(false);
  };

  // Delete API call
  const handleDeleteUser = async (userId) => {
    if (isDeleting) return; // prevent double-click

    try {
      setIsDeleting(true);

      const response = await axios.post(`${apiUrl}/authentication/deleteuser`, null, {
        params: { userId },
      });
      console.log("statusText" + response.statusText);
      if (response.status === 200) {
        toggleToasts(createToast('success', 'User Deleted Successfully.'));
        handleCloseDeleteModal();
        window.location.reload();
      }
    } catch (error) {
      toggleToasts(createToast('error', 'Failed to delete user.'));
    } finally {
      setIsDeleting(false);
    }
  };

  // Table column structure
  const columns = [
    { heading: 'Active', field: (row) => (<UserToggle key={row.id} user={row} />) },
    { heading: 'User Id', field: (row) => `${row.attUserId ?? row.email}` },
    {
      heading: 'Name', field: (row) => {
        let fullName = `${row?.firstName ?? ''} ${row?.lastName ?? ''}`;
        let email = row?.email !== null ? `<${row?.email}>` : '';

        return row?.email !== null
          ? (<a href={`mailto:${row?.email}`}>{fullName}{email}</a>)
          : `${fullName}`;
      }
    },
    { heading: 'Organization', field: (row) => `${row.organization}` },
    { heading: 'Roles', field: (row) => `${userDisplayValues[row.role]}` },
    {
      heading: 'Actions', field: (row) => (
        <>
          <Button
            size="sm"
            href={`${process.env.REACT_APP_BASE_APP_PATH}/users/${row.id}`}
            variant="outline-primary">
            Manage Schedule
          </Button>
        </>
      )
    },
    {
      heading: '', field: (row) => (
        <>
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <button
              className="user-delete-button"
              onClick={() => handleOpenDeleteModal(row.id)}
            >
              <img
                src={DeleteIcon}
                alt="Delete"
                className="close-icon-image"
              />
            </button>
          </div>
        </>
      )
    }
  ];

  const tableHeading = (
    <tr>
      {columns.map((column, idx) => <th key={idx}>{column.heading}</th>)}
    </tr>
  );

  const tableData = (users.length > 0)
    ? (
      <Table striped bordered hover variant="dark">
        <thead>{tableHeading}</thead>
        <tbody>
          {users.map((row) => (
            <tr key={row.id} className={`${!row.isActive ? 'text-muted' : ''}`}>
              {columns.map((column, idx) => (
                <td key={idx} className={`${!row.isActive ? 'text-muted' : ''}`}>
                  {column.field(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </Table>
    )
    : (<p className="text-center">There are currently no users to manage.</p>);

  const pageBody = (jwtToken && isAdmin)
    ? (
      <>
        <div className="d-flex">
          <h1 className="p-2 flex-grow-1">Users</h1>
          <div className="p-2 align-self-center justify-content-center">&nbsp;</div>
        </div>
        {tableData}
      </>
    ) : (
      <p>You are not allowed to view this page.</p>
    );

  return (
    <div className="user-page">
      <Container>
        <Row>
          <Col>
            {pageBody}
          </Col>
        </Row>
      </Container>

      {/* Delete Confirmation Modal */}
      <Modal
        show={showDeleteModal}
        onHide={handleCloseDeleteModal}
        centered
        backdrop="static"
        className="delete-confirmation-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title className="fw-bold text-danger">
            <i className="bi bi-exclamation-triangle-fill me-2"></i>
            Confirm Delete
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          <p className="fs-5 mb-2">
            Are you sure you want to delete this user?
          </p>
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-center">
          <Button variant="secondary" onClick={handleCloseDeleteModal}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={() => handleDeleteUser(selectedUserId)}
            disabled={isDeleting}
          >
            {isDeleting ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" />
                Deleting...
              </>
            ) : (
              'Yes, Delete'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default UsersPage;
